/**
 * What was said, and the audio it was said in.
 *
 * Two reads, merged into one column: `…/transcript` gives the segments and the
 * marks, `…/recording` gives the chunks. A mark is a jump point rather than a
 * row of its own decoration — somebody pressed Ctrl+Shift+B because that moment
 * mattered, and the only useful thing to do with it afterwards is go there.
 *
 * ── Chunks play in order, and the order is time ─────────────────────────────
 * Every participant records its own microphone, so the recording is N streams
 * of short files rather than one mixed track. Flattened by `offsetMs` and
 * played end to end, that is the meeting — in the order it happened, with each
 * file's own header, which is why `MediaRecorder` is restarted per chunk on the
 * writing side.
 *
 * A speaker's name comes from the server's stored `displayName`; a guest's is
 * drawn as a guest's, here as everywhere.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ds } from '../../ds';
import { useT } from '../../i18n';
import { formatDuration } from '../../i18n/format';
import { participantLabel } from '../../lib/relay/types';
import {
  chunkUrl,
  recording as readRecording,
  transcript as readTranscript,
  type Chunk,
  type Mark,
  type TranscriptSegment
} from './api';
import '../call/call.css';

/** A chunk with the person it belongs to, which is what the player needs and
 *  the grouped answer does not carry per row. */
interface Playable extends Chunk {
  participantId: string;
  displayName: string;
}

const GUESTS = /^g_/;

/** A `g_` id is a guest; the transcript carries no `guest` flag of its own and
 *  the id is what the roster keys on. */
const labelOf = (participantId: string, displayName: string): string =>
  participantLabel({ displayName, guest: GUESTS.test(participantId) });

export function TranscriptPanel({
  meetingId,
  guestToken
}: {
  meetingId: string;
  guestToken?: string;
}) {
  const { Button } = ds();
  const t = useT();
  const [segments, setSegments] = useState<TranscriptSegment[] | null>(null);
  const [marks, setMarks] = useState<Mark[]>([]);
  const [chunks, setChunks] = useState<Playable[]>([]);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState<number | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    let cancelled = false;

    void readTranscript(meetingId, guestToken)
      .then((answer) => {
        if (cancelled) return;
        setSegments(answer.segments);
        setMarks(answer.marks);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    void readRecording(meetingId, guestToken)
      .then((answer) => {
        if (cancelled) return;
        const flat = answer.participants.flatMap((p) =>
          p.chunks.map<Playable>((c) => ({
            ...c,
            participantId: p.participantId,
            displayName: p.displayName
          }))
        );
        flat.sort((a, b) => a.offsetMs - b.offsetMs);
        setChunks(flat);
      })
      .catch(() => {
        // A meeting with no recording has no chunks, which is not a failure —
        // the transcript is the thing this panel is for.
      });

    return () => {
      cancelled = true;
    };
  }, [meetingId, guestToken]);

  /** The segments and the marks on one clock. */
  const rows = useMemo(() => merge(segments ?? [], marks), [segments, marks]);

  const playFrom = (atMs: number) => {
    const index = chunks.findIndex((c) => c.offsetMs + c.durationMs > atMs);
    if (index < 0) return;
    setPlaying(index);
    const el = audio.current;
    if (!el) return;
    el.src = chunkUrl(meetingId, chunks[index]!.id);
    el.currentTime = Math.max(0, (atMs - chunks[index]!.offsetMs) / 1000);
    void el.play();
  };

  if (failed) {
    return <p style={{ color: 'var(--text-muted)' }}>{t('call.transcript.none')}</p>;
  }

  if (!segments) {
    return <p style={{ color: 'var(--text-muted)' }}>{t('app.loading')}</p>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-8)' }}>
        <div className="mv-call-menu-label" style={{ flex: 1, padding: 0 }}>
          {t('call.transcript.title')}
        </div>
        {chunks.length > 0 && (
          <Button size="sm" variant="secondary" onClick={() => playFrom(0)}>
            {t('call.transcript.play')}
          </Button>
        )}
      </div>

      {/* One element for the whole recording: when a chunk ends the next one's
          URL is set on the same element, which is what "in order" is. */}
      <audio
        ref={audio}
        controls={playing !== null}
        style={{ width: '100%', marginTop: 'var(--space-8)' }}
        onEnded={() => {
          const next = (playing ?? -1) + 1;
          if (next >= chunks.length) {
            setPlaying(null);
            return;
          }
          setPlaying(next);
          const el = audio.current;
          if (!el) return;
          el.src = chunkUrl(meetingId, chunks[next]!.id);
          void el.play();
        }}
      />

      {rows.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>{t('call.transcript.none')}</p>
      ) : (
        rows.map((row) =>
          row.kind === 'mark' ? (
            <div key={`m-${row.mark.id}`} style={{ padding: 'var(--space-8) 0' }}>
              <button
                type="button"
                className="mv-transcript-mark"
                onClick={() => playFrom(row.at)}
              >
                {formatDuration(row.at / 1000)} ·{' '}
                {row.mark.label || labelOf(row.mark.participantId, row.mark.displayName)}
              </button>
            </div>
          ) : (
            <div key={`s-${row.segment.participantId}-${row.at}`} className="mv-transcript-row">
              <span className="mv-transcript-who">
                {formatDuration(row.at / 1000)} ·{' '}
                {labelOf(row.segment.participantId, row.segment.displayName)}
              </span>
              <span>{row.segment.text}</span>
            </div>
          )
        )
      )}
    </div>
  );
}

type Row =
  | { kind: 'segment'; at: number; segment: TranscriptSegment }
  | { kind: 'mark'; at: number; mark: Mark };

/** Both lists on one clock, marks first where the times tie — a mark is a
 *  heading for what follows it. */
function merge(segments: TranscriptSegment[], marks: Mark[]): Row[] {
  const rows: Row[] = [
    ...segments.map<Row>((segment) => ({ kind: 'segment', at: segment.startMs, segment })),
    ...marks.map<Row>((mark) => ({ kind: 'mark', at: mark.atMs, mark }))
  ];
  rows.sort((a, b) => a.at - b.at || (a.kind === 'mark' ? -1 : 1));
  return rows;
}
